<script setup>
defineOptions({
  name: 'ContractAwardSelectionModal',
});

/*
|--------------------------------------------------------------------------
| ContractAwardSelectionModal.vue
|--------------------------------------------------------------------------
| Purpose
|   Modal for selecting a contract from the "Gunning invoeren" eligible list.
|   Used by the dashboard tile to navigate to the award page.
|
| Usage (parent):
|   <ContractAwardSelectionModal v-model="showModal" @select="onSelect" />
|
| Props
|   - modelValue (Boolean): controls modal visibility via v-model
|
| Events
|   - update:modelValue(Boolean): emitted when the modal opens/closes
|   - select(Object): emitted when the user chooses a contract
|   - close: emitted when modal is closed
|
| Workflow
|   1) Setup & routing helper
|   2) Props & emits
|   3) Reactive state
|   4) Methods: handleSelect, close
*/

import { ref, computed, getCurrentInstance } from 'vue'
import { router } from '@inertiajs/vue3'
import Modal from '@/components/Modal.vue'
import FormRichSelect from '@/components/Form/FormRichSelect.vue'

/* Routing helper (Ziggy route) */
const instance = getCurrentInstance()
const route = instance?.appContext.config.globalProperties.route

/* Props & Emits */
const props = defineProps({
  /** Controls modal visibility - accepts boolean or truthy value (e.g. tile key) */
  modelValue: { type: [Boolean, String], default: false },
})

const emit = defineEmits(['update:modelValue', 'select', 'close'])

/* v-model adapter for modal visibility - converts to boolean for Modal component */
const show = computed({
  get: () => !!props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

/* Reactive state */
const selectedContract = ref(null)

/* Search URL for FormRichSelect remote search */
const searchUrl = computed(() => route('contracts.award.data'))

/**
 * Handle contract selection
 * Redirects to award page for selected contract
 *
 * @route GET /contracts/{contract}/award
 */
function handleSelect() {
  if (!selectedContract.value) return

  const contractId = selectedContract.value
  const url = route('contracts.award.create', { contract: contractId })

  emit('select', { contract_id: contractId })
  router.visit(url)
  close()
}

/**
 * Close the modal and reset state
 */
function close() {
  show.value = false
  selectedContract.value = null
  emit('close')
}
</script>

<template>
  <Modal
    :show="show"
    title="Gunning invoeren"
    size="2xl"
    theme="orange"
    confirm-button-variant="orange"
    :confirm-button-disabled="!selectedContract"
    :show-default-footer="true"
    @close="close"
    @cancel="close"
    @confirm="handleSelect"
    :cancel-button-id="'input-gunning-modal-cancel'"
    :confirm-button-id="'input-gunning-modal-select'"
  >
    <div class="contract-selection-modal-content">
      <!-- Description -->
      <p class="modal-description">
        Selecteer een contract om de gunning in te voeren.
      </p>

      <!-- Contract Dropdown -->
      <div class="contract-selection-field">
        <label for="input-gunning-contract-selection" class="form-label">
          Contract
        </label>
        <FormRichSelect
          id="input-gunning-contract-selection"
          v-model="selectedContract"
          :search-url="searchUrl"
          placeholder="Zoek op inkoopcontractnummer of titel..."
          option-label="label"
          option-value="value"
          option-layout="card"
          class="wide-dropdown"
        />
      </div>
    </div>
  </Modal>
</template>